import {
  Button as DesignSystemButton,
  Clock,
} from "@ews-admin/global-design-system";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { Select } from "../../atoms/Select";
import { pinApi } from "../../../services/pin.api";
import {
  pinLockMinutesChanged,
  selectHasPin,
  selectPinLockMinutes,
} from "../../../store/reducers/sessionSlice";
import { PIN_LOCK_MINUTES_OPTIONS } from "../../../utils/pin-utils";

/**
 * MED-1644: idle delay before the PIN lock, from the settings page. Shown
 * only once the user has a PIN. The saved value goes into the session, so the
 * idle timer follows it without a reload.
 */
export const LockDelaySection: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const hasPin = useSelector(selectHasPin);
  const savedMinutes = useSelector(selectPinLockMinutes);
  const [minutes, setMinutes] = useState(savedMinutes);
  const [saving, setSaving] = useState(false);

  if (hasPin !== true) return null;

  const save = async () => {
    setSaving(true);
    try {
      const { lockMinutes } = await pinApi.updateLockDelay(minutes);
      dispatch(pinLockMinutesChanged(lockMinutes));
      setMinutes(lockMinutes);
      toast.success(t("pinLock.lockDelay.success"));
    } catch {
      // Surfaced by the axios interceptor.
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="p-5 space-y-4 bg-white rounded-xl border border-neutral-200">
      <div className="flex items-center space-x-2">
        <Clock className="w-4 h-4 text-neutral-500" />
        <h2 className="text-lg font-semibold text-neutral-900">
          {t("pinLock.lockDelay.title")}
        </h2>
      </div>
      <p className="text-xs text-neutral-500">{t("pinLock.lockDelay.hint")}</p>
      <Select
        label={t("pinLock.lockDelay.label")}
        value={String(minutes)}
        options={PIN_LOCK_MINUTES_OPTIONS.map(value => ({
          value: String(value),
          label: t("pinLock.lockDelay.option", { count: value }),
        }))}
        onChange={value => setMinutes(Number(value))}
      />
      <DesignSystemButton
        type="button"
        loading={saving}
        disabled={saving || minutes === savedMinutes}
        onClick={() => void save()}
      >
        {t("common.save")}
      </DesignSystemButton>
    </section>
  );
};
